const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function isISODate(value: string | null | undefined): value is string {
  return !!value && ISO_DATE.test(value)
}

export function toISODate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function parseISODateLocal(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number)
  return new Date(year, month - 1, day)
}

export function todayISODate(): string {
  return toISODate(new Date())
}

export function isTodayISODate(iso: string): boolean {
  return iso === todayISODate()
}

export function addDaysISO(iso: string, days: number): string {
  const date = parseISODateLocal(iso)
  date.setDate(date.getDate() + days)
  return toISODate(date)
}

/** Paula mock: "Fri Sep 12, 2026" */
export function formatLongDate(iso: string): string {
  return parseISODateLocal(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function formatMonthYear(year: number, monthIndex: number): string {
  return new Date(year, monthIndex, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  })
}

export function addMonths(year: number, monthIndex: number, delta: number): { year: number, monthIndex: number } {
  const date = new Date(year, monthIndex + delta, 1)
  return { year: date.getFullYear(), monthIndex: date.getMonth() }
}

export function startOfMonthISO(year: number, monthIndex: number): string {
  return toISODate(new Date(year, monthIndex, 1))
}

export function endOfMonthISO(year: number, monthIndex: number): string {
  return toISODate(new Date(year, monthIndex + 1, 0))
}

/** Inclusive from/to covering `before` months before through `after` months after. */
export function monthWindowISO(
  year: number,
  monthIndex: number,
  before = 1,
  after = 1,
): { from: string, to: string } {
  const start = addMonths(year, monthIndex, -before)
  const end = addMonths(year, monthIndex, after)
  return {
    from: startOfMonthISO(start.year, start.monthIndex),
    to: endOfMonthISO(end.year, end.monthIndex),
  }
}

export function unionIsoRange(
  a: { from: string, to: string },
  b: { from: string, to: string },
): { from: string, to: string } {
  return {
    from: a.from < b.from ? a.from : b.from,
    to: a.to > b.to ? a.to : b.to,
  }
}

/** Initial days-with-hours fetch: 12 months back through next month. */
export function initialHoursDaysRange(iso: string): { from: string, to: string } {
  const date = parseISODateLocal(isISODate(iso) ? iso : todayISODate())
  return monthWindowISO(date.getFullYear(), date.getMonth(), 12, 1)
}

export interface CalendarCell {
  iso: string
  day: number
  inMonth: boolean
}

/** Sunday-first month grid, padded to complete weeks. */
export function calendarMonthCells(year: number, monthIndex: number): CalendarCell[] {
  const startPad = new Date(year, monthIndex, 1).getDay()
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  const gridStart = new Date(year, monthIndex, 1 - startPad)
  const total = Math.ceil((startPad + daysInMonth) / 7) * 7
  const cells: CalendarCell[] = []
  for (let i = 0; i < total; i += 1) {
    const date = new Date(gridStart)
    date.setDate(gridStart.getDate() + i)
    cells.push({
      iso: toISODate(date),
      day: date.getDate(),
      inMonth: date.getMonth() === monthIndex,
    })
  }
  return cells
}
